import parseAnalyticsDateInput from "./parseAnalyticsDateInput";

interface IValidateDateRangeDraftArgs {
  draftFromStr: string;
  draftToStr: string;
  savedFromStr: string;
  savedToStr: string;
  savedFrom: Date;
  savedTo: Date;
  todayAtMidnight: Date;
}

interface IDateRangeDraftValidation {
  isFromInvalid: boolean;
  isToInvalid: boolean;
  hasPendingChanges: boolean;
  canApply: boolean;
  nextFrom: Date;
  nextTo: Date;
}

/**
 * Combined validity check for the custom date-range editor.
 *
 * Each input is invalid when its draft string was edited and the resulting
 * date is unparseable, lies in the future, or breaks the from <= to
 * invariant against the other field. Apply is enabled only when at least
 * one field changed and neither is invalid.
 *
 * Pure helper so the component stays small and the cross-field rules are
 * directly unit-testable.
 */
const validateDateRangeDraft = ({
  draftFromStr,
  draftToStr,
  savedFromStr,
  savedToStr,
  savedFrom,
  savedTo,
  todayAtMidnight,
}: IValidateDateRangeDraftArgs): IDateRangeDraftValidation => {
  const parsedDraftFrom = parseAnalyticsDateInput(draftFromStr);
  const parsedDraftTo = parseAnalyticsDateInput(draftToStr);

  const hasFromChanged = draftFromStr !== savedFromStr;
  const hasToChanged = draftToStr !== savedToStr;

  const nextFrom = parsedDraftFrom ?? savedFrom;
  const nextTo = parsedDraftTo ?? savedTo;

  const isFromInvalid = hasFromChanged && (!parsedDraftFrom || parsedDraftFrom > todayAtMidnight || parsedDraftFrom > nextTo);
  const isToInvalid = hasToChanged && (!parsedDraftTo || parsedDraftTo > todayAtMidnight || parsedDraftTo < nextFrom);

  const hasPendingChanges = hasFromChanged || hasToChanged;
  const canApply = hasPendingChanges && !isFromInvalid && !isToInvalid;

  return { isFromInvalid, isToInvalid, hasPendingChanges, canApply, nextFrom, nextTo };
};

export default validateDateRangeDraft;
